import {
  Eye,
  Trash2,
  MoreVertical,
  Edit,
  Zap,
  Shield,
  RefreshCw,
  ShieldAlert,
  Copy,
  CopyPlus,
  AlertTriangle,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { getIntegrationImage } from '@/lib/utils/integration-utils';
import { TpaLiveStatus } from '@/components/tagadapay/tpa-live-status';
import { PaymentFlowItemWithProcessors } from '@/lib/api/paymentFlows';
import { useOrgNavigate } from '@/hooks/use-app-navigate';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { toast } from 'react-hot-toast';

interface PaymentFlowCardProps {
  flow: PaymentFlowItemWithProcessors;
  onDelete: (flow: PaymentFlowItemWithProcessors) => void;
  onDuplicate: (flow: PaymentFlowItemWithProcessors) => void;
}

const getUniqueProcessors = (flow: PaymentFlowItemWithProcessors) => {
  const processors = new Map();

  flow.processorConfigs?.forEach((config) => {
    if (config.processor && !config.disabled) {
      processors.set(config.processor.id, config.processor);
    }
  });

  flow.fallbackProcessorConfigs?.forEach((config) => {
    if (config.processor) {
      processors.set(config.processor.id, config.processor);
    }
  });

  return Array.from(processors.values());
};

const isSmartRetryEnabledAndValid = (flow: PaymentFlowItemWithProcessors) => {
  const rebillSettings = (flow as { rebillSettings?: { customRetries?: unknown[] } }).rebillSettings;
  if (!rebillSettings) return false;
  if (!rebillSettings.customRetries || rebillSettings.customRetries.length === 0) return false;
  return true;
};

const isAbuseDetectionEnabledAndValid = (flow: PaymentFlowItemWithProcessors) => {
  const abuseDetectionConfig = (flow as { abuseDetectionConfig?: { enabled?: boolean } }).abuseDetectionConfig;
  if (!abuseDetectionConfig) return false;
  return abuseDetectionConfig.enabled === true;
};

const hasArchivedProcessors = (flow: PaymentFlowItemWithProcessors) => {
  const archivedInPrimary = flow.processorConfigs?.some((c: { processor?: { archivedAt?: Date | null } }) => c.processor?.archivedAt);
  const archivedInFallback = flow.fallbackProcessorConfigs?.some((c: { processor?: { archivedAt?: Date | null } }) => c.processor?.archivedAt);
  return archivedInPrimary || archivedInFallback;
};

/**
 * Org-level Payment Flow Card
 * Displays payment flow info without store-specific context
 */
export const PaymentFlowCard = ({ flow, onDelete, onDuplicate }: PaymentFlowCardProps) => {
  const { t } = useTranslation(['paymentFlows', 'common']);
  const { orgRoutes, navigate } = useOrgNavigate();
  const processors = getUniqueProcessors(flow);
  const smartRetryEnabled = isSmartRetryEnabledAndValid(flow);
  const abuseDetectionEnabled = isAbuseDetectionEnabledAndValid(flow);
  const hasArchived = hasArchivedProcessors(flow);

  return (
    <Card
      className={cn(
        'group relative flex h-full cursor-pointer flex-col gap-0 overflow-hidden py-0 transition-all hover:shadow-md hover:border-muted-foreground/30',
      )}
      onClick={() => navigate(orgRoutes.PAYMENT_FLOW_DETAILS(flow.id))}
    >
      <CardHeader className="p-4 pb-2">
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <CardTitle className="truncate text-base font-semibold">{flow.name}</CardTitle>
            {hasArchived && (
              <Badge 
                variant="outline" 
                className="shrink-0 gap-1 border-amber-300 bg-amber-100 px-1.5 py-0 text-[10px] font-medium text-amber-700 dark:border-amber-700 dark:bg-amber-900/50 dark:text-amber-300"
                title={t('paymentFlows:warnings.has_archived_processors')}
              >
                <AlertTriangle className="size-3" />
              </Badge>
            )}
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 shrink-0"
                onClick={(e) => e.stopPropagation()}
              >
                <MoreVertical className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-[180px]">
              <DropdownMenuItem asChild>
                <Link to={orgRoutes.PAYMENT_FLOW_DETAILS(flow.id)} className="cursor-pointer">
                  <Eye className="mr-2 size-4" />
                  {t('paymentFlows:view_details')}
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to={orgRoutes.PAYMENT_FLOW_EDIT(flow.id)} className="cursor-pointer">
                  <Edit className="mr-2 size-4" />
                  {t('common:edit')}
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  navigator.clipboard.writeText(flow.id);
                  toast.success(t('common:copied'));
                }}
                className="cursor-pointer"
              >
                <Copy className="mr-2 size-4" />
                {t('common:copy_id')}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  onDuplicate(flow);
                }}
                className="cursor-pointer"
              >
                <CopyPlus className="mr-2 size-4" />
                {t('paymentFlows:duplicate.action')}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(flow);
                }}
                className="text-destructive focus:text-destructive cursor-pointer"
              >
                <Trash2 className="mr-2 size-4" />
                {t('common:delete')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardHeader>

      <CardContent className="flex-1 space-y-3 p-4 pt-0">
        {/* Feature badges + strategy in one row */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-muted-foreground mr-1 text-sm">
            {flow.strategy === 'weighted'
              ? t('paymentFlows:badges.custom_routing')
              : t('paymentFlows:badges.auto_routing')}
          </span>
          {flow.threeDsEnabled && (
            <Badge
              variant="secondary"
              className="gap-1.5 border-0 bg-blue-100 px-2 py-0.5 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300"
            >
              <Shield className="size-3.5" />
              3DS
            </Badge>
          )}
          {flow.fallbackMode && (
            <Badge
              variant="secondary"
              className="gap-1.5 border-0 bg-amber-100 px-2 py-0.5 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300"
            >
              <RefreshCw className="size-3.5" />
              {t('paymentFlows:badges.fallback_retries', { count: flow.maxFallbackRetries ?? 0 })}
            </Badge>
          )}
          {smartRetryEnabled && (
            <Badge
              variant="secondary"
              className="gap-1.5 border-0 bg-purple-100 px-2 py-0.5 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300"
            >
              <Zap className="size-3.5" />
              {t('paymentFlows:badges.smart_retry')}
            </Badge>
          )}
          {abuseDetectionEnabled && (
            <Badge
              variant="secondary"
              className="gap-1.5 border-0 bg-rose-100 px-2 py-0.5 text-rose-700 dark:bg-rose-900/50 dark:text-rose-300"
            >
              <ShieldAlert className="size-3.5" />
              {t('paymentFlows:badges.fraud_protection')}
            </Badge>
          )}
        </div>

        {/* Processors */}
        {processors.length > 0 && (
          <div className="space-y-1.5">
            <span className="text-muted-foreground text-xs font-medium uppercase tracking-wide">
              {t('paymentFlows:card.processors_heading')}
            </span>
            <div className="flex flex-wrap gap-2">
              {processors.slice(0, 4).map((processor) => (
                <Link
                  key={processor.id}
                  to={orgRoutes.PROCESSOR_DETAILS(processor.id)}
                  className="bg-muted/30 hover:bg-accent hover:border-primary/20 flex items-center gap-2 rounded-md border px-2.5 py-1.5 transition-all"
                  onClick={(e) => e.stopPropagation()}
                >
                  <img
                    src={getIntegrationImage(processor.type)}
                    alt={processor.type}
                    className="size-4 object-contain"
                  />
                  <span className="max-w-[120px] truncate text-sm font-medium">{processor.name}</span>
                  <TpaLiveStatus
                    tpaId={processor.tagadapayAccountId}
                    status={processor.tagadapayStatus}
                    mode={processor.tagadapayMode}
                    compact
                  />
                </Link>
              ))}
              {processors.length > 4 && (
                <span className="text-muted-foreground flex items-center px-2 py-1 text-xs">
                  {t('paymentFlows:card.more_processors', { count: processors.length - 4 })}
                </span>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};


